import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  Field,
  Form,
  Icon,
  ModalCard,
  Radio,
  Table,
  TableTitle,
  Tooltip,
} from '~/components';
import { Card } from '~/components/ModalCard';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useDateTimeFormat } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';
import RevalueConfirmation from '../../rate-cards/RevalueConfirmation';
import { describeWindow } from '../../rate-cards/timeline';

const Modal = styled(ModalCard)`
  ${Card} {
    width: ${({ $wide }) => ($wide ? '52rem' : '42rem')};
  }
`;

export default function ProjectMemberBillRateModal({ project, projectMember, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const auth = useAuth();
  const dateTimeFormat = useDateTimeFormat();

  const projectMemberId = projectMember.id;

  const [member, setMember] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(project.id)
          .members(projectMemberId)
          .get();
        setMember(data);
      } catch {
        // Keep the modal in its loading state.
      }
    })();
  }, [api, workspace.id, project.id, projectMemberId]);

  // A project with a single rate card keeps the layout it had before rate cards existed.
  const hasMultipleRateCards = (member?.rateCards?.length ?? 0) > 1;

  const initialValues = mergeValues(
    { isBillable: project.isBillable, rateCards: [] },
    {
      ..._.pick(member ?? {}, ['isBillable']),
      rateCards: (member?.rateCards ?? []).map((rateCard) => ({ ...rateCard, rate: rateCard.rate ?? '' })),
    },
  );

  const handleSubmit = async (values, formik) => {
    const saved = _.keyBy(member.rateCards, 'projectRateCardId');
    const rateCards = values.rateCards
      .map((row) => emptyStringToNull(_.pick(row, ['projectRateCardId', 'rate'])))
      .filter((row) => row.rate !== (saved[row.projectRateCardId].rate ?? null));

    const tracked = rateCards.map((row) => saved[row.projectRateCardId]).filter(({ hasTimeEntries }) => hasTimeEntries);
    if (tracked.length > 0) {
      const confirmed = await confirmation.prompt((resolve) => (
        <RevalueConfirmation rateCards={hasMultipleRateCards ? tracked : null} resolve={resolve} />
      ));
      if (!confirmed) return;
    }

    formik.setSubmitting(true);

    try {
      await api.www
        .workspaces(workspace.id)
        .projectSettings(project.id)
        .members(projectMemberId)
        .upsert({ isBillable: values.isBillable, rateCards });
      toast.success('Project Member saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
      formik.setSubmitting(false);
    }
  };

  return (
    <Modal title="Edit Bill Rate" byline={projectMember.member?.name} onClose={onClose} $wide={hasMultipleRateCards}>
      {!member ? (
        <ModalCard.Body>
          <PageLoader />
        </ModalCard.Body>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            isBillable: Yup.bool(),
            rateCards: Yup.array().of(
              Yup.object().shape({
                rate: Yup.number().label('Bill Rate').min(0).max(99999999999).nullable(),
              }),
            ),
          })}>
          {({ values, ...formik }) => (
            <Form>
              <ModalCard.Body>
                <Form.Control>
                  <Field.RadioGroup name="isBillable">
                    <Radio value={true} label="Billable" disabled={!auth.projects.sections.billing} />
                    <Radio value={false} label="Non-Billable" disabled={!auth.projects.sections.billing} />
                  </Field.RadioGroup>
                </Form.Control>

                {!hasMultipleRateCards ? (
                  values.isBillable && (
                    <Form.Control>
                      <Field.Currency
                        name="rateCards.0.rate"
                        placeholder="Bill Rate"
                        precision={7}
                        currency={project.currency}
                        disabled={!auth.projects.sections.billing}
                      />
                    </Form.Control>
                  )
                ) : (
                  <>
                    <TableTitle>Rate Cards</TableTitle>

                    <Table small>
                      <Table.Header>
                        <Table.Column>Date Range</Table.Column>
                        <Table.Column width="12rem" align="right">
                          Bill Rate
                        </Table.Column>
                      </Table.Header>

                      <Table.Body>
                        {values.rateCards.map((row, index) => (
                          <Table.Row key={row.projectRateCardId} data-testid="rate_card_rate_row">
                            <Table.Cell>
                              #{row.projectRateCard.number} · {describeWindow(row, dateTimeFormat)}
                              {row.hasTimeEntries && (
                                <Tooltip Container="span" message="Time has been tracked in this period">
                                  <Icon icon="clock" color={colors.grey40} spaceLeft data-testid="tracked_time" />
                                </Tooltip>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {values.isBillable && (
                                <Field.Currency
                                  name={`rateCards.${index}.rate`}
                                  precision={7}
                                  currency={project.currency}
                                  disabled={!auth.projects.sections.billing}
                                />
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </>
                )}
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onClose}>Close</CancelButton>

                  <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                    Save &amp; Close
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
}
